<template>
	<div class="row px-6 pb-8">
		<AppCard inset no-header class="add-product-buttons">
			<template v-slot:body>
				<div class="wrapper-buttons-add">
					<div class="row-button-add">
						<AppButton icon-left="icon-plus" small outline :text="getString('new')" @click="activeAddNewListProduct = true" />
						<AppButton icon-left="icon-pointer-hand" small outline :text="getString('existent')" @click="activeAddExistentListProduct = true" />
					</div>
				</div>
			</template>
		</AppCard>
		<AppBottomSheet :force-width="bottomSheetWidthSmall" :active="activeAddNewListProduct" @toggling-popup="toggleBottomSheets">
			<ModalHeader :title="getString('add_new_product')" :button-text="getString('add')" @click-button="clickAddNew" />
			<AppList :items="newProductItems">
				<template v-slot:input-text="{item}">
					<div class="row list-row">
						<p class="list-item-title">{{ item.title }}</p>
						<AppTextInput class="text-input" ref="new-prod-name-input" @changed-text="changedNewProdName" :placeholder="item.title" />
					</div>
				</template>
				<template v-slot:prod-price="{item}">
					<div class="row list-row">
						<p class="list-item-title">{{ item.title }}</p>
						<AppNumberInput double-decimal :label="getString('price_symbol', [currencyStore.currCurrency.symbol])" class="number-input" ref="new-prod-price-input" @changed-value="changeNewProdPrice" :min="0" />
					</div>
				</template>
			</AppList>
		</AppBottomSheet>
		<AppBottomSheet :force-width="bottomSheetWidthSmall" :active="activeAddExistentListProduct" @toggling-popup="toggleBottomSheets" >
			<MiniMenu :item-temp-id="itemTempId" :category-temp-id="categoryTempId" :list-temp-id="listTempId" v-if="activeAddExistentListProduct" @selected-items="addSelectedLinkedProducts" />
		</AppBottomSheet>
	</div>
</template>

<script>
import AppTabsSwitch from "@/shared/components/AppTabsSwitch.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppList from "@/shared/components/AppList.vue";
import AppCard from "@/shared/components/AppCard.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";

import {useMenuStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {getRandomAlphanumericString} from "@/shared/helpers/strings";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import MiniMenu from "@/console-new/components/config/menu/MiniMenu.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, menuStore, currencyStore, showNotification: notificationStore.showNotification};
	},
	mixins: [WindowWidth],
	name: "CompositeEditorAdd",
	emits: ['added-item'],
	components: { ModalHeader, MiniMenu, AppTabsSwitch, AppCard, AppButtonIcon, AppTextInput, AppNumberInput, AppBottomSheet, AppList, AppButton, AppClickableIcon },
	props: {
		categoryTempId: {
			type: String,
			required: true
		},
		itemTempId: {
			type: String,
			required: true
		},
		listTempId: {
			type: String,
			required: true
		},
	},
	data() {
		return {
			activeAddNewListProduct: false,
			activeAddExistentListProduct: false,
			newItemValue: "",
			newItemPrice: 0,
			newProductItems: [
				{
					title: this.getString('product_name'),
					customSlot: "input-text",
				},
				{
					title: this.getString('addition_price'),
					customSlot: "prod-price",
				}
			],
		}
	},
	computed: {
		bottomSheetWidthSmall() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "600px";
			}
		},
		menuItem() {
			return this.menuStore.modItemFromTempIds(this.categoryTempId, this.itemTempId);
		},
		list() {
			return this.menuItem.lists[this.listTempId];
		},
		listProducts() {
			return this.list.list_products;
		},
	},
	methods: {
		addSelectedLinkedProducts(selectedItems) {
			for(let item of selectedItems) {
				if(!this.listProducts[item.menu_item_id]) {
					this.addExistent(item, true);
				}
			}
			this.activeAddExistentListProduct = false;
			this.$emit('added-item');
		},
		toggleBottomSheets(active) {
			if(!active) {
				this.activeAddNewListProduct = false;
				this.activeAddExistentListProduct = false;
			}
		},
		changedNewProdName(value){
			this.newItemValue = value;
		},
		changeNewProdPrice(value){
			this.newItemPrice = this.currencyStore.actualToDbValue(value);
		},
		clickAddNew() {
			if(!this.newItemValue) {
				this.showNotification("composed-missing-name");
				return
			}
			let newListProd = {
				tempid: getRandomAlphanumericString(10),
				temp_product_id: getRandomAlphanumericString(10),
				customSlot: "summary-prod",
				product_name: this.newItemValue,
				title: this.newItemValue,
				product_price: this.newItemPrice,

				linked_name: false,
				linked_price: false,

				menu_item_list_id: this.list.menu_item_list_id,
				menu_item_list_product_id: null,
				menu_item_id: this.menuItem.menu_item_id,
				menu_item_linked_id: null,
				meta_product: true,

				order_number: this.listProducts ? Object.keys(this.listProducts).length : 0,
				deleted_product: false,
				available: true,
			}
			this.listProducts[newListProd.tempid] = newListProd;
			this.showNotification("composed-list-product-added");
			this.newItemValue = "";
			this.newItemPrice = 0;
			this.$refs['new-prod-name-input'].$refs['input'].value = "";
			this.$refs['new-prod-price-input'].changeValue(0);
			this.activeAddNewListProduct = false;
		},
		addExistent(sourceItem, added) {
			if(added) {
				let newListProd = {
					tempid: getRandomAlphanumericString(10),
					temp_product_id: getRandomAlphanumericString(10),
					customSlot: "summary-prod",
					product_name: sourceItem.name,
					linked_name: true,
					linked_price: true,
					title: sourceItem.name,
					product_price: sourceItem.price,
					menu_item_list_id: this.list.menu_item_list_id,
					menu_item_list_product_id: null,
					menu_item_id: this.menuItem.menu_item_id,
					menu_item_linked_id: sourceItem.menu_item_id,
					meta_product: false,
					order_number: this.listProducts ? Object.keys(this.listProducts).length : 0,
					deleted_product: false,
					available: true,
				}
				sourceItem.linkedTempId = newListProd.tempid;
				this.listProducts[newListProd.tempid] = newListProd;
			} else {
				delete this.listProducts[sourceItem.linkedTempId];
			}

		},
	}

}
</script>

<style scoped lang="scss">
.add-product-buttons:deep(.card-body) {
	padding: 20px;
	@media (max-width: 768px) {
		padding: 20px 10px;
	}
}
.existent-prod-title {
	@include truncateString;
	width: calc(100% - 140px);
	margin-right: auto;

	@media (max-width: 768px) {
		font-size: 14px;
	}
	@media (max-width: 560px) {
		font-size: 12px;
	}
	@media (max-width: 400px) {
		font-size: 10px;
		width: calc(100% - 120px);
	}
}
.existent-prod-price {
	padding-right: 5px;
	width: 80px;
	text-align: right;

	@media (max-width: 400px) {
		width: 60px;
		font-size: 12px;
	}
}
.container-existent {
	@include customScrollbar;
	padding: 1px 0;
	overflow-y: auto;
	max-height: 50vh;

	.existent-list {
		max-height: 100%;
	}
	&:deep(.list-item) {
		min-height: 45px;
		&:first-of-type:before,
		&:last-of-type:after {
			display: none;
		}
	}
}
.controls {
	width: 40px;
}
.add-btn:hover, .add-btn:active {
	box-shadow: none;
}

.list-row {
	@include centered;
	justify-content: space-between;
	padding: 10px 0;
}
.text-input {
	margin-left: auto;

	@media (max-width: 560px) {
		max-width: 200px;
	}
	@media (max-width: 480px) {
		max-width: 180px;
	}
	@media (max-width: 420px) {
		max-width: 150px;
	}
	@media (max-width: 350px) {
		margin-top: 6px;
		max-width: 100%;
		width: 100%;
	}
}
.list-item-title {
	max-width: calc(100% - 105px);
	font-size: 16px;

	@media (max-width: 560px) {
		font-size: 14px;
	}
	@media (max-width: 400px) {
		font-size: 12px;
	}
	@media (max-width: 350px) {
		max-width: 100%;
		width: 100%;
	}
}
@media (max-width: 350px) {
	.list-row {
		flex-wrap: wrap;

		.number-input {
			margin-top: 6px;
			margin-left: auto;
		}
	}
}
.wrapper-buttons-add {
	height: 100%;
	width: 100%;
	.row-button-add {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		width: 100%;

		@media (max-width: 320px) {
			flex-direction: column-reverse;
			gap: 10px;
		}
	}
	.base-button {
		height: 40px;
		width: calc(50% - 5px);
		max-width: unset;
		background-color: var(--color-bg-primary-2);

		&:hover {
			box-shadow: none;
		}
		@media (max-width: 320px) {
			width: 100%;
		}
	}
}
</style>